import { useEffect, useMemo, useRef, useState } from 'react'
import {
  CategoryIcon,
  CATEGORY_ICON_OPTIONS,
} from '@/components/CategoryIcon'
import {
  StableFieldEditor,
  blankField,
  toApiFields,
} from '@/components/FieldEditor'
import { SeedHtmlEditor } from '@/components/SeedHtmlEditor'
import { childrenOf, parentOptions } from '@/lib/categories'
import {
  blockDisplayTitle,
  canMoveBlockRow,
  defaultBlockTitle,
  defaultLayoutForCategory,
  moveBlockRow,
  newBlockId,
  nextBlockY,
  resolveOverlaps,
} from '@/lib/pageLayout'

const DEFAULT_TABLE_COLUMNS = [
  { label: 'Name', input_type: 'text' },
  { label: 'URL', input_type: 'url' },
  { label: 'Password', input_type: 'secret' },
]

const KIND_LABELS = {
  rich: 'Rich text',
  table: 'Table',
  devices: 'Devices (Dash)',
}

function serializeLayoutBlocks(blocks, categoryColumns = []) {
  return blocks.map((b) => ({
    id: b.id,
    type: b.type,
    title: blockDisplayTitle(b),
    x: Number(Number(b.x).toFixed(2)),
    y: Number(Number(b.y).toFixed(2)),
    w: Number(Number(b.w).toFixed(2)),
    content_html:
      b.type === 'rich' && b.id !== 'main-rich' ? b.content_html || '' : null,
    columns:
      b.type === 'table'
        ? (b.columns?.length ? b.columns : categoryColumns || []).map((c) => ({
            label: c.label,
            key: c.key || undefined,
            input_type: c.input_type || 'text',
            wide: Boolean(c.wide),
          }))
        : null,
  }))
}

function richHtmlForBlock(block, cat) {
  if (block.id === 'main-rich') return cat.content_html || '<p></p>'
  return block.content_html || '<p></p>'
}

function SampleTableChrome({ columns, title }) {
  const cols = columns?.length ? columns : [{ label: 'Column' }]
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold text-brand-muted">
        {title || 'Sample table'} — column headers only (no live rows in
        templates)
      </div>
      <table className="min-w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-brand-muted">
          <tr>
            {cols.map((c, i) => (
              <th key={`${c.label}-${i}`} className="px-3 py-2 font-semibold">
                {c.label || 'Untitled'}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="text-brand-muted">
            <td
              colSpan={cols.length}
              className="px-3 py-6 text-center text-xs italic"
            >
              Sample preview — rows are filled per customer
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

/**
 * Full-screen WYSIWYG editor for one template category (draft only).
 * Save category writes into the parent draft; Save template still publishes.
 */
export function TemplateCategoryEditor({
  category,
  allCategories,
  resetKey,
  onClose,
  onSave,
}) {
  const [local, setLocal] = useState(() => structuredClone(category))
  const [layout, setLayout] = useState(() => defaultLayoutForCategory(category))
  const [layoutEditing, setLayoutEditing] = useState(false)
  const [selectedBlockId, setSelectedBlockId] = useState(null)
  const [dirty, setDirty] = useState(false)
  const [drag, setDrag] = useState(null)
  const [heights, setHeights] = useState({})
  const canvasRef = useRef(null)
  const blockEls = useRef({})
  const layoutHeightsRef = useRef({})
  const layoutEditingRef = useRef(false)

  useEffect(() => {
    setLocal(structuredClone(category))
    setLayout(defaultLayoutForCategory(category))
    setLayoutEditing(false)
    setSelectedBlockId(null)
    setDirty(false)
    setDrag(null)
    setHeights({})
    layoutHeightsRef.current = {}
    layoutEditingRef.current = false
  }, [category.slug, resetKey])

  useEffect(() => {
    layoutEditingRef.current = layoutEditing
  }, [layoutEditing])

  useEffect(() => {
    const observers = []
    layout.blocks.forEach((block) => {
      const el = blockEls.current[block.id]
      if (!el || typeof ResizeObserver === 'undefined') return
      const ro = new ResizeObserver((entries) => {
        // Freeze measurements during layout edit — TipTap/width reflow
        // otherwise feeds a height loop that shakes the canvas.
        if (layoutEditingRef.current) return
        const h = entries[0]?.contentRect?.height
        if (!h) return
        setHeights((prev) =>
          prev[block.id] === Math.round(h)
            ? prev
            : { ...prev, [block.id]: Math.round(h) },
        )
      })
      ro.observe(el)
      observers.push(ro)
    })
    return () => observers.forEach((ro) => ro.disconnect())
  }, [layout.blocks])

  useEffect(() => {
    if (layoutEditing || drag) return
    if (!Object.keys(heights).length) return
    setLayout((prev) => {
      const packed = resolveOverlaps(prev.blocks, heights)
      if (packed.length !== prev.blocks.length) {
        return { ...prev, blocks: packed }
      }
      const byId = Object.fromEntries(prev.blocks.map((b) => [b.id, b]))
      const same = packed.every((b) => {
        const o = byId[b.id]
        return (
          o &&
          Math.abs((Number(o.y) || 0) - (Number(b.y) || 0)) < 1 &&
          Math.abs((Number(o.x) || 0) - (Number(b.x) || 0)) < 0.1 &&
          Math.abs((Number(o.w) || 0) - (Number(b.w) || 0)) < 0.1
        )
      })
      if (same) return prev
      return { ...prev, blocks: packed }
    })
  }, [heights, layoutEditing, drag])

  useEffect(() => {
    if (!drag) return undefined
    const overlapHeights = layoutHeightsRef.current

    function onMove(e) {
      const canvas = canvasRef.current
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const dxPct = ((e.clientX - drag.startX) / rect.width) * 100
      const dyPx = e.clientY - drag.startY
      let nextX = drag.originX + dxPct
      let nextY = drag.originY + dyPx
      nextX = Math.max(0, Math.min(100 - drag.blockW, nextX))
      nextY = Math.max(0, Math.min(7500, nextY))
      setLayout((prev) => ({
        ...prev,
        blocks: prev.blocks.map((b) =>
          b.id === drag.blockId ? { ...b, x: nextX, y: nextY } : b,
        ),
      }))
    }

    function onUp() {
      setLayout((prev) => ({
        ...prev,
        blocks: resolveOverlaps(prev.blocks, overlapHeights),
      }))
      setDrag(null)
      setDirty(true)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [drag])

  const parents = useMemo(
    () => parentOptions(allCategories, local.slug),
    [allCategories, local.slug],
  )
  const hasChildren = childrenOf(allCategories, local.slug).length > 0

  const overlapHeights = layoutEditing ? layoutHeightsRef.current : heights

  const canvasMinHeight = Math.max(
    480,
    ...layout.blocks.map(
      (b) =>
        (Number(b.y) || 0) +
        (overlapHeights[b.id] || heights[b.id] || 320) +
        24,
    ),
  )

  const selectedBlock = layout.blocks.find((b) => b.id === selectedBlockId)

  function beginLayoutEdit() {
    layoutHeightsRef.current = { ...heights }
    layoutEditingRef.current = true
    setSelectedBlockId(layout.blocks[0]?.id || null)
    setLayoutEditing(true)
  }

  function patchLocal(patch) {
    setLocal((prev) => ({ ...prev, ...patch }))
    setDirty(true)
  }

  function setRichHtml(blockId, html) {
    if (blockId === 'main-rich') {
      patchLocal({ content_html: html })
      return
    }
    setLayout((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) =>
        b.id === blockId ? { ...b, content_html: html } : b,
      ),
    }))
    setDirty(true)
  }

  function setTableColumns(blockId, apiColumns) {
    const tableCount = layout.blocks.filter((b) => b.type === 'table').length
    const isPrimary = blockId === 'main-table' || tableCount <= 1
    setLayout((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) =>
        b.id === blockId ? { ...b, columns: apiColumns } : b,
      ),
    }))
    if (isPrimary) {
      patchLocal({ columns: apiColumns })
    } else {
      setDirty(true)
    }
  }

  function startDrag(e, block) {
    if (!layoutEditing) return
    e.preventDefault()
    e.stopPropagation()
    setSelectedBlockId(block.id)
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId)
    } catch {
      // ignore
    }
    setDrag({
      blockId: block.id,
      startX: e.clientX,
      startY: e.clientY,
      originX: Number(block.x) || 0,
      originY: Number(block.y) || 0,
      blockW: Number(block.w) || 100,
    })
  }

  function setBlockWidth(blockId, w) {
    setLayout((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) => {
        if (b.id !== blockId) return b
        const width = Math.max(20, Math.min(100, w))
        const x = Math.min(Number(b.x) || 0, 100 - width)
        return { ...b, w: width, x }
      }),
    }))
    setDirty(true)
  }

  function finishWidthAdjust() {
    setLayout((prev) => ({
      ...prev,
      blocks: resolveOverlaps(prev.blocks, layoutHeightsRef.current),
    }))
  }

  function moveBlockVertical(blockId, direction) {
    if (!layoutEditing) return
    setSelectedBlockId(blockId)
    setLayout((prev) => ({
      ...prev,
      blocks: moveBlockRow(prev.blocks, blockId, direction, layoutHeightsRef.current),
    }))
    setDirty(true)
  }

  function tidyLayout() {
    setLayout((prev) => ({
      ...prev,
      blocks: resolveOverlaps(prev.blocks, layoutHeightsRef.current),
    }))
    setDirty(true)
  }

  function addRichBlock() {
    const id = newBlockId('rich')
    setLayout((prev) => ({
      ...prev,
      blocks: resolveOverlaps(
        [
          ...prev.blocks,
          {
            id,
            type: 'rich',
            title: defaultBlockTitle('rich'),
            x: 0,
            y: nextBlockY(prev.blocks, layoutHeightsRef.current),
            w: 100,
            content_html: '<p></p>',
          },
        ],
        layoutHeightsRef.current,
      ),
    }))
    layoutHeightsRef.current = {
      ...layoutHeightsRef.current,
      [id]: 220,
    }
    setSelectedBlockId(id)
    setDirty(true)
  }

  function addTableBlock() {
    setLayout((prev) => {
      const hasTable = prev.blocks.some((b) => b.type === 'table')
      const columns = hasTable
        ? DEFAULT_TABLE_COLUMNS.map((c) => ({ ...c }))
        : local.columns?.length
          ? local.columns.map((c) => ({ ...c }))
          : DEFAULT_TABLE_COLUMNS.map((c) => ({ ...c }))
      const id = hasTable ? newBlockId('table') : 'main-table'
      // First table on a rich page → treat as a table category again.
      if (!hasTable && local.kind === 'rich') {
        setLocal((cat) => ({ ...cat, kind: 'table', columns }))
      } else if (!hasTable) {
        setLocal((cat) => ({ ...cat, columns }))
      }
      layoutHeightsRef.current = {
        ...layoutHeightsRef.current,
        [id]: 260,
      }
      setSelectedBlockId(id)
      return {
        ...prev,
        blocks: resolveOverlaps(
          [
            ...prev.blocks,
            {
              id,
              type: 'table',
              title: defaultBlockTitle('table'),
              x: 0,
              y: nextBlockY(prev.blocks, layoutHeightsRef.current),
              w: 100,
              columns,
            },
          ],
          layoutHeightsRef.current,
        ),
      }
    })
    setDirty(true)
  }

  function removeBlock(blockId) {
    const removing = layout.blocks.find((b) => b.id === blockId)
    if (!removing) return

    let nextBlocks = layout.blocks.filter((b) => b.id !== blockId)
    let tablesLeft = nextBlocks.filter((b) => b.type === 'table')
    let richLeft = nextBlocks.filter((b) => b.type === 'rich')

    // Removing the last table: convert to a rich text category (e.g. DKIM → notes).
    if (removing.type === 'table' && !tablesLeft.length) {
      let contentHtml = local.content_html || '<p></p>'
      if (!richLeft.length) {
        nextBlocks = [
          {
            id: 'main-rich',
            type: 'rich',
            title: defaultBlockTitle('rich'),
            x: 0,
            y: 0,
            w: 100,
          },
        ]
        layoutHeightsRef.current = {
          ...layoutHeightsRef.current,
          'main-rich': 220,
        }
        richLeft = nextBlocks
      } else if (!richLeft.some((b) => b.id === 'main-rich')) {
        const first = richLeft[0]
        contentHtml = first.content_html || contentHtml
        nextBlocks = nextBlocks.map((b) =>
          b.id === first.id ? { ...b, id: 'main-rich' } : b,
        )
      }
      setLocal((cat) => ({
        ...cat,
        kind: 'rich',
        columns: [],
        content_html: contentHtml,
      }))
      tablesLeft = []
      richLeft = nextBlocks.filter((b) => b.type === 'rich')
    }

    // Removing main notes when other rich blocks remain — promote another.
    if (
      removing.id === 'main-rich' &&
      removing.type === 'rich' &&
      richLeft.length &&
      !richLeft.some((b) => b.id === 'main-rich')
    ) {
      const first = richLeft[0]
      const html = first.content_html || local.content_html || '<p></p>'
      nextBlocks = nextBlocks.map((b) =>
        b.id === first.id ? { ...b, id: 'main-rich' } : b,
      )
      setLocal((cat) => ({ ...cat, content_html: html }))
    }

    if (!nextBlocks.length) {
      window.alert('This page needs at least one block.')
      return
    }

    // Don't leave a rich page with zero notes and only tables handled above;
    // don't leave empty.
    if (
      removing.type === 'rich' &&
      !nextBlocks.some((b) => b.type === 'rich') &&
      !nextBlocks.some((b) => b.type === 'table')
    ) {
      window.alert('This page needs at least one block.')
      return
    }

    setLayout({
      version: layout.version || 1,
      blocks: resolveOverlaps(nextBlocks, layoutHeightsRef.current),
    })
    setSelectedBlockId((cur) =>
      cur === blockId ? nextBlocks[0]?.id || null : cur,
    )
    setDirty(true)
  }

  function finishLayoutEdit() {
    setLayout((prev) => ({
      ...prev,
      blocks: resolveOverlaps(prev.blocks, layoutHeightsRef.current),
    }))
    layoutEditingRef.current = false
    setLayoutEditing(false)
    setSelectedBlockId(null)
    setDirty(true)
  }

  function handleSave() {
    if (local.kind === 'devices') {
      onSave({
        ...local,
        title: (local.title || '').trim() || local.slug,
        icon: local.icon || 'document',
        columns: [],
        content_html: '',
        page_layout: null,
      })
      return
    }

    const tidied = resolveOverlaps(
      layout.blocks,
      layoutEditing ? layoutHeightsRef.current : heights,
    )
    const tableBlocks = tidied.filter((b) => b.type === 'table')
    const primaryTable =
      tableBlocks.find((b) => b.id === 'main-table') || tableBlocks[0]

    // Kind follows remaining blocks so removing the last table can become rich text.
    const kind = tableBlocks.length ? 'table' : 'rich'

    let columns = local.columns || []
    if (kind === 'table' && primaryTable?.columns?.length) {
      columns = primaryTable.columns
    } else if (kind === 'table' && !columns.length) {
      columns = toApiFields([blankField()])
    } else if (kind === 'rich') {
      columns = tableBlocks.length ? columns : []
    }

    const content_html =
      kind === 'rich' ? local.content_html || '<p></p>' : ''

    // Always persist layout so move/resize positions survive Save template.
    const page_layout = tidied.length
      ? {
          version: 1,
          blocks: serializeLayoutBlocks(tidied, columns),
        }
      : null

    onSave({
      ...local,
      kind,
      title: (local.title || '').trim() || local.slug,
      columns: kind === 'rich' && !tableBlocks.length ? [] : columns,
      content_html,
      page_layout,
      icon: local.icon || 'document',
    })
  }

  function handleClose() {
    if (dirty && !window.confirm('Discard unsaved changes to this category?')) {
      return
    }
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-100">
      <header className="shrink-0 border-b border-slate-200 bg-white px-4 py-3 shadow-sm sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-brand-muted">
              Template category · {KIND_LABELS[local.kind] || local.kind}
            </p>
            <input
              value={local.title}
              onChange={(e) => patchLocal({ title: e.target.value })}
              className="mt-1 w-full max-w-xl rounded border border-slate-300 bg-white px-3 py-2 font-display text-2xl tracking-tight text-brand"
              aria-label="Category title"
            />
            <p className="mt-1 text-xs text-brand-muted">
              Slug: {local.slug}. Changes apply to the draft until you use{' '}
              <strong>Save template</strong> on the templates page.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-brand hover:bg-slate-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Save category
            </button>
          </div>
        </div>
      </header>

      <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-wrap items-end gap-4">
          <label className="text-sm">
            <span className="text-brand-muted">Icon</span>
            <select
              value={local.icon || 'document'}
              onChange={(e) => patchLocal({ icon: e.target.value })}
              className="mt-1 block rounded border border-slate-300 bg-white px-3 py-2"
            >
              {CATEGORY_ICON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <div className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-brand">
            <CategoryIcon name={local.icon || 'document'} className="h-5 w-5" />
          </div>
          <label className="flex items-center gap-2 text-sm text-brand">
            <input
              type="checkbox"
              checked={Boolean(local.parent_slug)}
              disabled={hasChildren || parents.length === 0}
              onChange={(e) => {
                if (!e.target.checked) {
                  patchLocal({ parent_slug: null })
                } else if (parents[0]) {
                  patchLocal({ parent_slug: parents[0].slug })
                }
              }}
            />
            Sub-category in the menu
          </label>
          {Boolean(local.parent_slug) && (
            <label className="text-sm">
              <span className="text-brand-muted">Parent</span>
              <select
                value={local.parent_slug || ''}
                onChange={(e) =>
                  patchLocal({ parent_slug: e.target.value || null })
                }
                className="mt-1 block rounded border border-slate-300 bg-white px-3 py-2"
              >
                {parents.map((p) => (
                  <option key={p.slug} value={p.slug}>
                    {p.title}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="mx-auto max-w-6xl space-y-3">
          {local.kind === 'devices' ? (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-sm text-brand-muted shadow-sm">
              Device lists come from Bekat Dash when a customer is created. Only
              the title and icon are editable for this category in the template.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                {!layoutEditing ? (
                  <button
                    type="button"
                    onClick={beginLayoutEdit}
                    className="rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                  >
                    Edit layout
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={addRichBlock}
                      className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
                    >
                      Add rich text
                    </button>
                    <button
                      type="button"
                      onClick={addTableBlock}
                      className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
                    >
                      Add data table
                    </button>
                    <button
                      type="button"
                      onClick={tidyLayout}
                      className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
                    >
                      Fix overlaps
                    </button>
                    {selectedBlock && (
                      <label
                        className="flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-xs text-sky-950"
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        Width ({blockDisplayTitle(selectedBlock)})
                        <input
                          type="range"
                          min={20}
                          max={100}
                          value={Math.round(selectedBlock.w)}
                          onChange={(e) =>
                            setBlockWidth(
                              selectedBlock.id,
                              Number(e.target.value),
                            )
                          }
                          onPointerUp={finishWidthAdjust}
                          onKeyUp={finishWidthAdjust}
                          className="w-40"
                        />
                        <span className="w-8 tabular-nums">
                          {Math.round(selectedBlock.w)}%
                        </span>
                      </label>
                    )}
                    <span className="max-w-sm text-xs text-brand-muted">
                      Use Up/Down to reorder sections (or drag :: Move). Select a
                      block, then use Width here.
                    </span>
                    <button
                      type="button"
                      onClick={finishLayoutEdit}
                      className="ml-auto rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                    >
                      Done
                    </button>
                  </>
                )}
              </div>

              <div
                ref={canvasRef}
                className={[
                  'relative w-full rounded-xl border border-slate-300 bg-slate-200/70 p-1',
                  layoutEditing ? 'ring-2 ring-sky-300 ring-offset-2' : '',
                ].join(' ')}
                style={{ minHeight: `${canvasMinHeight}px` }}
              >
                {layout.blocks.length === 0 && (
                  <p className="absolute inset-0 flex items-center justify-center text-sm text-brand-muted">
                    No blocks yet — click Edit layout, then add rich text or a
                    data table.
                  </p>
                )}

                {layout.blocks.map((block) => {
                  const frozenH = layoutHeightsRef.current[block.id]
                  return (
                  <div
                    key={block.id}
                    ref={(el) => {
                      if (el) blockEls.current[block.id] = el
                      else delete blockEls.current[block.id]
                    }}
                    onClick={() => {
                      if (layoutEditing) setSelectedBlockId(block.id)
                    }}
                    className={[
                      'absolute rounded-lg border border-slate-300 bg-white shadow-sm',
                      block.type === 'table'
                        ? 'overflow-visible'
                        : 'overflow-hidden',
                      layoutEditing ? 'border-sky-500' : '',
                      layoutEditing && selectedBlockId === block.id
                        ? 'ring-2 ring-sky-500'
                        : '',
                      drag?.blockId === block.id
                        ? 'z-30 shadow-md ring-2 ring-sky-400'
                        : 'z-10',
                    ].join(' ')}
                    style={{
                      left: `${block.x}%`,
                      top: `${Number(block.y) || 0}px`,
                      width: `${block.w}%`,
                      minHeight: layoutEditing && frozenH
                        ? `${frozenH}px`
                        : '8rem',
                      height: layoutEditing && frozenH ? `${frozenH}px` : undefined,
                    }}
                  >
                    {!layoutEditing && (
                      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-100/90 px-3 py-1.5">
                        <span className="min-w-0 truncate text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                          {blockDisplayTitle(block)}
                        </span>
                      </div>
                    )}
                    {layoutEditing && (
                      <div
                        className="flex cursor-grab items-center gap-2 overflow-x-auto whitespace-nowrap border-b border-sky-200 bg-sky-100 px-2 py-2 active:cursor-grabbing"
                        onPointerDown={(e) => startDrag(e, block)}
                      >
                        <span
                          className="shrink-0 select-none rounded bg-sky-600 px-2 py-1 text-xs font-bold tracking-wide text-white"
                          aria-hidden
                        >
                          :: Move
                        </span>
                        <span
                          className="flex shrink-0 items-center gap-1"
                          onPointerDown={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="rounded border border-sky-400 bg-white px-2 py-1 text-xs font-semibold text-sky-950 hover:bg-sky-50 disabled:opacity-40"
                            title="Move section up"
                            aria-label={`Move ${blockDisplayTitle(block)} up`}
                            disabled={!canMoveBlockRow(layout.blocks, block.id, -1)}
                            onClick={() => moveBlockVertical(block.id, -1)}
                          >
                            Up
                          </button>
                          <button
                            type="button"
                            className="rounded border border-sky-400 bg-white px-2 py-1 text-xs font-semibold text-sky-950 hover:bg-sky-50 disabled:opacity-40"
                            title="Move section down"
                            aria-label={`Move ${blockDisplayTitle(block)} down`}
                            disabled={!canMoveBlockRow(layout.blocks, block.id, 1)}
                            onClick={() => moveBlockVertical(block.id, 1)}
                          >
                            Down
                          </button>
                        </span>
                        <label
                          className="flex min-w-0 flex-1 items-center gap-1 text-xs text-sky-950"
                          onPointerDown={(e) => e.stopPropagation()}
                        >
                          Title
                          <input
                            value={block.title ?? blockDisplayTitle(block)}
                            onChange={(e) => {
                              setLayout((prev) => ({
                                ...prev,
                                blocks: prev.blocks.map((b) =>
                                  b.id === block.id
                                    ? { ...b, title: e.target.value }
                                    : b,
                                ),
                              }))
                              setDirty(true)
                            }}
                            className="min-w-0 flex-1 rounded border border-sky-300 bg-white px-2 py-1 text-xs font-semibold text-brand"
                          />
                        </label>
                        <span className="shrink-0 text-xs capitalize text-sky-900/70">
                          {block.type}
                        </span>
                        {!(
                          block.id === 'main-rich' && block.type === 'rich'
                        ) && (
                          <button
                            type="button"
                            className="ml-auto shrink-0 text-xs font-medium text-red-700 underline"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={() => removeBlock(block.id)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                    <div className="p-2">
                      {layoutEditing ? (
                        <div
                          className="flex items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-xs text-brand-muted"
                          style={{
                            minHeight: frozenH
                              ? `${Math.max(80, frozenH - 56)}px`
                              : '10rem',
                          }}
                        >
                          {block.type === 'rich'
                            ? 'Rich text seed — edit after Done'
                            : 'Data table — edit fields after Done'}
                        </div>
                      ) : block.type === 'rich' ? (
                        <div className="space-y-2">
                          <p className="px-1 text-xs text-brand-muted">
                            Seed notes — merge fields like{' '}
                            <code className="rounded bg-slate-100 px-1">
                              {'{{it_manager}}'}
                            </code>
                          </p>
                          <SeedHtmlEditor
                            resetKey={`${resetKey}:${local.slug}:${block.id}`}
                            value={richHtmlForBlock(block, local)}
                            onChange={(html) => setRichHtml(block.id, html)}
                          />
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <StableFieldEditor
                            resetKey={`${resetKey}:${local.slug}:${block.id}`}
                            columns={
                              block.columns?.length
                                ? block.columns
                                : local.columns
                            }
                            onChange={(apiCols) =>
                              setTableColumns(block.id, apiCols)
                            }
                          />
                          <SampleTableChrome
                            columns={
                              block.columns?.length
                                ? block.columns
                                : local.columns
                            }
                            title={blockDisplayTitle(block)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
